import { useEffect, useRef, useState } from 'react';
import { BackdropScene } from '../three/BackdropScene';
import { prefersReducedMotion } from '../three/state';
import { resolveTask } from '../lib/loader';

/**
 * Fixed full-viewport canvas hosting the persistent 3D scene. Isolated leaf:
 * no Motion in this tree, Three.js owns every frame here.
 */
export function WebGLBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  // Reduced motion: a single still frame that belongs to the hero and
  // scrolls away with it, instead of following the reader down the page.
  const [reduce] = useState(prefersReducedMotion);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let scene: BackdropScene;
    try {
      scene = new BackdropScene(canvas, { reduced: reduce });
    } catch {
      // No WebGL: the page still works, it just loses the backdrop.
      setFailed(true);
      resolveTask('backdrop');
      return;
    }
    let alive = true;
    scene
      .warmUp()
      .then(() => alive && scene.start())
      .finally(() => resolveTask('backdrop'));
    return () => {
      alive = false;
      scene.destroy();
    };
  }, []);

  if (failed) return null;
  return (
    <canvas
      ref={ref}
      aria-hidden
      className={`pointer-events-none z-0 w-full ${
        reduce ? 'absolute inset-x-0 top-0 h-[100dvh]' : 'fixed inset-0 h-full'
      }`}
    />
  );
}
